import React, { FunctionComponent, MutableRefObject, useState } from 'react'
import { Country } from '@afterpay/types'
import { Icon, Text } from '@afterpaytouch/core'
import { useCountry } from '../../../../state'
import { usePCLAsLowAsText, usePCLFromText, usePCLNumberOfInstalmentsText } from '../../helper'
import styles from './style.module.css'
import { useTranslation } from 'next-i18next'
import { useCheckoutSizes } from '../../../../utils/responsive'
import { useMonitoringWithEnduringEventProperties } from '../../../../integrations/monitoring'
import { TrackingEvent } from '../../../../model/monitoring'

interface ConsumerLendingRadioLabelProps {
  buttonRef: MutableRefObject<null>
  showTooltip: boolean
  setShowTooltip: (bool: boolean) => void
  hasClickedOutside: boolean
  setHasClickedOutside: (bool: boolean) => void
  payInMoreAPRAriaDescribedBy: string
}

export const ConsumerLendingRadioLabel: FunctionComponent<ConsumerLendingRadioLabelProps> = ({
  buttonRef,
  showTooltip,
  setShowTooltip,
  hasClickedOutside,
  setHasClickedOutside,
  payInMoreAPRAriaDescribedBy,
}) => {
  const { t } = useTranslation()
  const { textSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()

  const paymentHeaderTextPCL = usePCLFromText()
  const paymentIntervalTextPCL = usePCLNumberOfInstalmentsText()
  const paymentAPRComparisonPCL = usePCLAsLowAsText()

  const [hasSentTooltipAnalytics, setHasSentTooltipAnalytics] = useState(false)

  const onMouseDownPayInMoreAPRInAU = (): void => {
    setHasClickedOutside(false)
  }

  const onClickPayInMoreAPRInAU = (): void => {
    if (!hasSentTooltipAnalytics) {
      logEvent(TrackingEvent.PRESSED_CL_ENTRY_PAGE_COMPARISON_TOOLTIP)
      setHasSentTooltipAnalytics(true)
    }
    if (!hasClickedOutside) {
      setShowTooltip(!showTooltip)
    }
    setHasClickedOutside(false)
  }

  const payInMoreAPRInAU = (): JSX.Element => (
    <div className={styles.payInMoreAPRInAUWrapper}>
      <div className={styles.payInMoreAPRInAUContainer}>
        <Text size={textSize} color='Gray50' bold>
          {paymentAPRComparisonPCL}
          <button
            type='button'
            className={styles.tooltipTriggerButton}
            ref={buttonRef}
            onClick={onClickPayInMoreAPRInAU}
            onMouseDown={onMouseDownPayInMoreAPRInAU}
            data-testid='pay-in-more-apr-info-button'
            aria-label={t('consumerLending:paymentOptions.payInMoreAPRAriaLabel')}
            aria-describedby={payInMoreAPRAriaDescribedBy}
            aria-expanded={showTooltip}
          >
            <Icon.Info color='Gray50' size='ExtraSmall' />
          </button>
        </Text>
      </div>
    </div>
  )

  const payInMoreAPRInUS = (): JSX.Element => (
    <div className={styles.interestText}>
      <Text size={textSize} color='Gray50'>
        {paymentAPRComparisonPCL}
      </Text>
    </div>
  )

  return (
    <div className={styles.headingText}>
      <Text size={textSize} bold>
        {paymentHeaderTextPCL}
      </Text>
      <div className={styles.downPaymentText}>
        <Text size={textSize} color='Gray50'>
          {paymentIntervalTextPCL}
        </Text>
      </div>
      {country === Country.AU && payInMoreAPRInAU()}
      {country === Country.US && payInMoreAPRInUS()}
    </div>
  )
}
